import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { InductionTemplatesManager } from './InductionTemplatesManager';
import { GraduationCap, Plus, Users, CalendarDays, CheckCircle2, Send, Copy, Trash2, Pencil, FileDown, Clock, Star, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HR_DEPTS = ['Human Resources', 'Human Resources Management'];

export const indStatusClass = (s) => s === 'Confirmed' ? 'bg-emerald-600 text-white hover:bg-emerald-600'
  : s === 'Draft' ? 'bg-slate-800 text-white hover:bg-slate-800'
  : s === 'Pending Approval' ? 'bg-amber-500 text-white hover:bg-amber-500'
  : s === 'Completed' ? 'bg-violet-600 text-white hover:bg-violet-600'
  : 'bg-muted text-foreground';

const Stat = ({ icon: Icon, label, value, tint }) => (
  <Card data-testid={`induction-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-5 flex items-center gap-4">
      <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${tint}`}><Icon className="h-5 w-5" /></div>
      <div><p className="text-2xl font-bold leading-none">{value ?? 0}</p><p className="text-xs text-muted-foreground mt-1">{label}</p></div>
    </CardContent>
  </Card>
);

const AnalyticsPane = ({ getAuthHeader }) => {
  const [a, setA] = useState(null);
  useEffect(() => {
    (async () => {
      const r = await fetch(`${API}/inductions/analytics`, { headers: getAuthHeader() });
      if (r.ok) setA(await r.json());
    })();
  }, [getAuthHeader]);
  if (!a) return <Skeleton className="h-40 w-full" />;
  const maxDept = Math.max(1, ...(a.department_stats || []).map(d => d.sessions));
  return (
    <div className="space-y-6" data-testid="induction-analytics">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={CheckCircle2} label="Acceptance Rate" value={`${a.acceptance_rate}%`} tint="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" />
        <Stat icon={Users} label="Attendance Rate" value={`${a.attendance_rate}%`} tint="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" />
        <Stat icon={Star} label="Avg Overall Rating" value={a.avg_overall} tint="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" />
        <Stat icon={TrendingUp} label="Feedback Received" value={a.feedback_count} tint="bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card><CardHeader><CardTitle className="text-base">Session Approvals</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span>Accepted</span><span className="font-semibold text-emerald-600">{a.sessions_accepted}</span></div>
            <div className="flex justify-between"><span>Rejected</span><span className="font-semibold text-rose-600">{a.sessions_rejected}</span></div>
            <div className="flex justify-between"><span>Pending</span><span className="font-semibold text-amber-600">{a.sessions_pending}</span></div>
            <div className="flex justify-between border-t pt-2"><span>Total Sessions</span><span className="font-semibold">{a.total_sessions}</span></div>
          </CardContent>
        </Card>
        <Card><CardHeader><CardTitle className="text-base">Ratings & Feedback</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span>Average Overall Rating</span><span className="font-semibold">{a.avg_overall} ★</span></div>
            <div className="flex justify-between"><span>Feedback Received</span><span className="font-semibold">{a.feedback_count}</span></div>
            <div className="flex justify-between"><span>Attendance Rate</span><span className="font-semibold">{a.attendance_rate}%</span></div>
          </CardContent>
        </Card>
      </div>
      <Card><CardHeader><CardTitle className="text-base">Sessions by Department</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(a.department_stats || []).length === 0 && <p className="text-sm text-muted-foreground">No sessions yet.</p>}
          {(a.department_stats || []).map(d => (
            <div key={d.department} className="flex items-center gap-3">
              <span className="text-xs w-48 truncate">{d.department}</span>
              <div className="flex-1 bg-muted rounded h-3"><div className="bg-blue-600 h-3 rounded" style={{ width: `${(d.sessions / maxDept) * 100}%` }} /></div>
              <span className="text-xs font-medium w-6 text-right">{d.sessions}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default function InductionsPage() {
  const { getAuthHeader, employee } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const isHR = employee?.role === 'Super Admin' || HR_DEPTS.includes(employee?.department);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, lRes] = await Promise.all([
        fetch(`${API}/inductions/dashboard`, { headers: getAuthHeader() }),
        fetch(`${API}/inductions`, { headers: getAuthHeader() }),
      ]);
      if (dRes.ok) setDashboard(await dRes.json());
      if (lRes.ok) setRows(await lRes.json());
    } catch (_e) { /* ignore */ } finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const publish = async (id) => {
    const r = await fetch(`${API}/inductions/${id}/publish`, { method: 'POST', headers: getAuthHeader() });
    if (r.ok) { const d = await r.json(); toast.success(`Sent for approval${d.owner_emails_sent ? ` · ${d.owner_emails_sent} owner email(s) sent` : ''}`); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Publish failed'); }
  };
  const duplicate = async (id) => {
    const r = await fetch(`${API}/inductions/${id}/duplicate`, { method: 'POST', headers: getAuthHeader() });
    if (r.ok) { const d = await r.json(); toast.success(`Copied as ${d.induction_id}`); load(); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this induction? This cannot be undone.')) return;
    const r = await fetch(`${API}/inductions/${id}`, { method: 'DELETE', headers: getAuthHeader() });
    if (r.ok) { toast.success('Induction deleted'); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Delete failed'); }
  };
  const downloadPdf = async (id) => {
    const r = await fetch(`${API}/inductions/${id}/pdf`, { headers: getAuthHeader() });
    if (!r.ok) { toast.error('PDF export failed'); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `Induction_${id}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const ListPane = (
    <Card>
      <CardHeader><CardTitle className="text-base">All Inductions</CardTitle></CardHeader>
      <CardContent>
        {loading ? (<div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground" data-testid="inductions-empty">
            <GraduationCap className="h-10 w-10 mx-auto mb-3 opacity-40" /><p>No inductions yet.</p>
            {isHR && <Button variant="outline" className="mt-4" onClick={() => navigate('/inductions/new')}>Create your first induction</Button>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>ID</TableHead><TableHead>Title</TableHead><TableHead>New Joiners</TableHead>
                <TableHead>Sessions</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.induction_id} data-testid={`induction-row-${r.induction_id}`} className="cursor-pointer" onClick={() => navigate(`/inductions/${r.induction_id}`)}>
                    <TableCell className="font-mono text-xs">{r.induction_id}</TableCell>
                    <TableCell className="font-medium">{r.title}</TableCell>
                    <TableCell className="max-w-[220px]">
                      <span className="text-sm truncate block" title={(r.employees || []).map(e => e.full_name).join(', ')}>
                        {(r.employees || []).length === 0 ? '—' : (r.employees || []).map(e => e.full_name).join(', ')}
                      </span>
                    </TableCell>
                    <TableCell>{r.session_count}</TableCell>
                    <TableCell><Badge className={indStatusClass(r.status)}>{r.status}</Badge></TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      {isHR && (
                        <div className="flex items-center justify-end gap-1">
                          {r.status === 'Draft' && (<>
                            <Button size="icon" variant="ghost" title="Edit" data-testid={`edit-${r.induction_id}`} onClick={() => navigate(`/inductions/${r.induction_id}/edit`)}><Pencil className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" title="Publish" data-testid={`publish-${r.induction_id}`} onClick={() => publish(r.induction_id)}><Send className="h-4 w-4 text-emerald-600" /></Button>
                          </>)}
                          <Button size="icon" variant="ghost" title="Download PDF" data-testid={`pdf-${r.induction_id}`} onClick={() => downloadPdf(r.induction_id)}><FileDown className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" title="Copy" data-testid={`copy-${r.induction_id}`} onClick={() => duplicate(r.induction_id)}><Copy className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" title="Delete" data-testid={`delete-${r.induction_id}`} onClick={() => remove(r.induction_id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="inductions-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><GraduationCap className="h-6 w-6" /> Induction Management</h1>
            <p className="text-muted-foreground">Plan, schedule and track new-joiner inductions.</p>
          </div>
          {isHR && <Button data-testid="new-induction-btn" onClick={() => navigate('/inductions/new')}><Plus className="h-4 w-4 mr-1.5" /> New Induction</Button>}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={CalendarDays} label="Total Inductions" value={dashboard?.total} tint="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200" />
          <Stat icon={Clock} label="Pending Approval" value={dashboard?.pending_approval} tint="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" />
          <Stat icon={CheckCircle2} label="Confirmed" value={dashboard?.confirmed} tint="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" />
          <Stat icon={Users} label="Total Sessions" value={dashboard?.total_sessions} tint="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" />
        </div>

        {isHR ? (
          <Tabs defaultValue="list">
            <TabsList><TabsTrigger value="list" data-testid="tab-list">Inductions</TabsTrigger><TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger><TabsTrigger value="templates" data-testid="tab-templates">Feedback Templates</TabsTrigger></TabsList>
            <TabsContent value="list" className="mt-4">{ListPane}</TabsContent>
            <TabsContent value="analytics" className="mt-4"><AnalyticsPane getAuthHeader={getAuthHeader} /></TabsContent>
            <TabsContent value="templates" className="mt-4"><InductionTemplatesManager /></TabsContent>
          </Tabs>
        ) : ListPane}
      </div>
    </Layout>
  );
}
